import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, Search, Users, Zap,
  ArrowUpRight, Sparkles, Target, Copy,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const StatCard = ({ label, value, sub, icon: Icon, tone = "slate", testid }) => (
  <div
    className={`rounded-sm border border-${tone}-200 bg-${tone}-50 p-3`}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
      {Icon && <Icon size={11} className={`text-${tone}-600`} />}
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-slate-900">
      {value ?? "—"}
    </div>
    {sub && <div className="mt-0.5 text-[10px] text-slate-500">{sub}</div>}
  </div>
);

const DiffBadge = ({ diff }) => {
  if (!diff) return <span className="text-[10px] text-slate-400">flat</span>;
  const positive = diff > 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const cls = positive ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded-sm ${cls}`}>
      <Icon size={9} />
      {positive ? "+" : ""}{diff}
    </span>
  );
};

export default function AdminSbtAIVisibility() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [units, setUnits] = useState(null);
  const [sugg, setSugg] = useState(null);
  const [suggLoading, setSuggLoading] = useState(true);
  const [suggBusy, setSuggBusy] = useState(false);
  const [manualComps, setManualComps] = useState(
    "apollo.io,seamless.ai,cognism.com,zoominfo.com,lusha.com,rocketreach.co,uplead.com,hunter.io,leadiq.com,adapt.io",
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sbt/ai-visibility/latest");
      setSnap(data && data.overview ? data : null);
      setUnits(data?.units_remaining ?? null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load snapshot");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSuggestions = useCallback(async () => {
    setSuggLoading(true);
    try {
      const { data } = await api.get("/admin/sbt/ai-visibility/suggested-keywords");
      setSugg(data && data.suggestions ? data : null);
    } catch (e) {
      // 409 = no snapshot yet; silent
    } finally {
      setSuggLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadSuggestions(); }, [load, loadSuggestions]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/sbt/ai-visibility/refresh");
      toast.success(`Snapshot captured · units left: ${data.units_remaining ?? "n/a"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const generateSuggestions = async () => {
    setSuggBusy(true);
    try {
      const trimmed = (manualComps || "").trim();
      const url = trimmed
        ? `/admin/sbt/ai-visibility/suggested-keywords/refresh?manual_competitors=${encodeURIComponent(trimmed)}`
        : "/admin/sbt/ai-visibility/suggested-keywords/refresh";
      const { data } = await api.post(url);
      toast.success(`${data.count} keywords · peers=${data.peer_source} · units left: ${data.units_remaining ?? "n/a"}`);
      await loadSuggestions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Suggestion generation failed");
    } finally {
      setSuggBusy(false);
    }
  };

  const copyKeyword = (kw) => {
    navigator.clipboard.writeText(kw).catch(() => {});
    toast.success(`Copied "${kw}"`);
  };

  const overview = snap?.overview || {};
  const keywords = snap?.keywords || [];
  const competitors = snap?.competitors || [];

  return (
    <div data-testid="admin-sbt-ai-visibility" className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Sparkles size={18} className="text-cyan-600" /> AI Visibility — SEMrush live rank
          </h2>
          <p className="text-[11px] text-muted-foreground mt-0.5 max-w-2xl">
            Nightly at 04:00 IST the CRM pulls SEMrush organic rank, top-20 keywords and top-10 organic competitors for
            <span className="font-mono ml-1">sbtresearch.com</span>. Click Refresh to force a snapshot now (~250 SEMrush units).
            {units !== null && <span className="ml-2 font-mono text-emerald-700">{units.toLocaleString()} SEMrush units remaining</span>}
          </p>
        </div>
        <Button
          size="sm"
          className="rounded-sm bg-cyan-600 hover:bg-cyan-700"
          onClick={refresh}
          disabled={refreshing}
          data-testid="ai-vis-refresh-btn"
        >
          {refreshing
            ? <Loader2 size={12} className="animate-spin mr-1" />
            : <RefreshCw size={12} className="mr-1" />}
          Refresh snapshot
        </Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-xs text-muted-foreground">
          <Loader2 size={14} className="animate-spin inline mr-1" /> Loading…
        </div>
      ) : !snap ? (
        <div className="py-10 text-center text-sm text-slate-600">
          No snapshot yet. Click <b>Refresh snapshot</b> to fetch one.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatCard label="Domain rank"       value={overview.rank?.toLocaleString()} sub="SEMrush DB rank" icon={Search} tone="cyan" testid="stat-rank" />
            <StatCard label="Organic keywords"  value={overview.organic_keywords?.toLocaleString()} sub={`in ${overview.database?.toUpperCase() || "US"} database`} icon={Zap} tone="emerald" testid="stat-org-kw" />
            <StatCard label="Organic traffic"   value={overview.organic_traffic?.toLocaleString()} sub="monthly visits" icon={ArrowUpRight} tone="sky" testid="stat-org-traffic" />
            <StatCard label="Competitors tracked" value={competitors.length} sub="top organic overlap" icon={Users} tone="amber" testid="stat-competitors" />
          </div>
          <p className="text-[10px] text-slate-500">
            Captured <span className="font-mono">{new Date(snap.captured_at).toLocaleString()}</span>
          </p>

          {/* Top keywords table */}
          <div className="border border-border rounded-sm bg-card">
            <div className="px-3 py-2 bg-secondary/50 border-b border-border">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                Top {keywords.length} organic keywords
              </span>
            </div>
            {keywords.length === 0 ? (
              <div className="p-6 text-xs text-slate-500 text-center">No keywords returned. Site may be too new for SEMrush to rank — check back next week.</div>
            ) : (
              <table className="w-full text-xs" data-testid="ai-vis-keywords-table">
                <thead className="bg-secondary/20 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-1.5">Keyword</th>
                    <th className="text-left px-3 py-1.5">Position</th>
                    <th className="text-left px-3 py-1.5">Δ</th>
                    <th className="text-left px-3 py-1.5">Volume/mo</th>
                    <th className="text-left px-3 py-1.5">CPC</th>
                    <th className="text-left px-3 py-1.5">Ranking URL</th>
                  </tr>
                </thead>
                <tbody>
                  {keywords.map((k, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`ai-vis-kw-row-${i}`}>
                      <td className="px-3 py-1.5 font-medium">{k.keyword}</td>
                      <td className="px-3 py-1.5 tabular-nums">#{k.position}</td>
                      <td className="px-3 py-1.5"><DiffBadge diff={k.position_diff} /></td>
                      <td className="px-3 py-1.5 tabular-nums">{k.search_volume?.toLocaleString()}</td>
                      <td className="px-3 py-1.5 tabular-nums">${k.cpc_usd?.toFixed(2)}</td>
                      <td className="px-3 py-1.5 truncate max-w-[240px]">
                        {k.url ? (
                          <a href={k.url} target="_blank" rel="noreferrer noopener" className="text-cyan-700 hover:underline">{k.url.replace(/^https?:\/\//, "")}</a>
                        ) : <span className="text-slate-400 italic">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Competitors */}
          <div className="border border-border rounded-sm bg-card">
            <div className="px-3 py-2 bg-secondary/50 border-b border-border">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                Top {competitors.length} organic competitors
              </span>
            </div>
            {competitors.length === 0 ? (
              <div className="p-6 text-xs text-slate-500 text-center">No competitor overlap yet.</div>
            ) : (
              <table className="w-full text-xs" data-testid="ai-vis-competitors-table">
                <thead className="bg-secondary/20 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-1.5">Domain</th>
                    <th className="text-left px-3 py-1.5">Relevance</th>
                    <th className="text-left px-3 py-1.5">Common KW</th>
                    <th className="text-left px-3 py-1.5">Their organic KW</th>
                    <th className="text-left px-3 py-1.5">Their organic traffic</th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((c, i) => (
                    <tr key={i} className="border-t border-border" data-testid={`ai-vis-comp-row-${i}`}>
                      <td className="px-3 py-1.5 font-medium">
                        <a href={`https://${c.domain}`} target="_blank" rel="noreferrer noopener" className="text-cyan-700 hover:underline">{c.domain}</a>
                      </td>
                      <td className="px-3 py-1.5 tabular-nums">{(c.competitor_relevance * 100).toFixed(1)}%</td>
                      <td className="px-3 py-1.5 tabular-nums">{c.common_keywords?.toLocaleString()}</td>
                      <td className="px-3 py-1.5 tabular-nums">{c.organic_keywords?.toLocaleString()}</td>
                      <td className="px-3 py-1.5 tabular-nums">{c.organic_traffic?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Suggested Keywords to Target ─────────────────────────── */}
          <div className="border border-border rounded-sm bg-card" data-testid="ai-vis-suggestions-panel">
            <div className="px-3 py-2 bg-gradient-to-r from-cyan-50 to-emerald-50 border-b border-border">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-700 flex items-center gap-1.5">
                    <Target size={12} className="text-emerald-600" />
                    Suggested Keywords to Target
                  </span>
                  <p className="text-[10px] text-slate-500 mt-0.5 max-w-2xl">
                    Ranked by opportunity score = (volume × CPC) ÷ (difficulty + 1). Mines your competitors&apos; most valuable keywords that <b>sbtresearch.com does not yet own</b>. Regenerating costs ≈ 600 SEMrush units.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {sugg?.age_hours != null && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${sugg.is_stale ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                      {sugg.is_stale ? "Stale" : "Fresh"} · {sugg.age_hours}h old
                    </span>
                  )}
                  <Button
                    size="sm"
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px]"
                    onClick={generateSuggestions}
                    disabled={suggBusy}
                    data-testid="ai-vis-generate-suggestions-btn"
                  >
                    {suggBusy
                      ? <Loader2 size={12} className="animate-spin mr-1" />
                      : <Sparkles size={12} className="mr-1" />}
                    {sugg ? "Regenerate" : "Generate suggestions"}
                  </Button>
                </div>
              </div>
              <div className="mt-2 flex flex-col gap-1">
                <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
                  Curated competitor domains (comma-separated)
                </label>
                <input
                  type="text"
                  value={manualComps}
                  onChange={(e) => setManualComps(e.target.value)}
                  placeholder="apollo.io, seamless.ai, cognism.com, zoominfo.com, lusha.com"
                  className="h-7 rounded-sm border border-slate-300 bg-white px-2 text-[11px] font-mono"
                  data-testid="ai-vis-manual-comps-input"
                />
                <p className="text-[10px] text-slate-500">
                  Leave blank to use SEMrush&apos;s auto-detected competitors. For niche/new domains, curated is dramatically better.
                  {sugg?.peer_source && sugg?.peer_domains && (
                    <span className="ml-1">Last run used <b>{sugg.peer_source}</b>: {sugg.peer_domains.join(", ")}</span>
                  )}
                </p>
              </div>
            </div>
            {suggLoading ? (
              <div className="p-6 text-xs text-slate-500 text-center">
                <Loader2 size={12} className="animate-spin inline mr-1" /> Loading…
              </div>
            ) : !sugg || !sugg.suggestions || sugg.suggestions.length === 0 ? (
              <div className="p-8 text-sm text-slate-600 text-center">
                No suggestions yet. Click <b>Generate suggestions</b> — this pulls every top-10 competitor&apos;s best-performing keywords and scores them by opportunity.
              </div>
            ) : (
              <table className="w-full text-xs" data-testid="ai-vis-suggestions-table">
                <thead className="bg-secondary/20 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-1.5 w-8">#</th>
                    <th className="text-left px-3 py-1.5">Keyword</th>
                    <th className="text-left px-3 py-1.5">Intent</th>
                    <th className="text-left px-3 py-1.5">Difficulty</th>
                    <th className="text-left px-3 py-1.5">Volume/mo</th>
                    <th className="text-left px-3 py-1.5">CPC</th>
                    <th className="text-left px-3 py-1.5">Top competitor</th>
                    <th className="text-left px-3 py-1.5">Opp. score</th>
                    <th className="text-right px-3 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {sugg.suggestions.map((s, i) => {
                    const kd = s.difficulty || 0;
                    const kdTone = kd < 30 ? "bg-emerald-100 text-emerald-800"
                                 : kd < 60 ? "bg-amber-100 text-amber-800"
                                 : "bg-rose-100 text-rose-800";
                    const intentTone = {
                      commercial: "bg-cyan-100 text-cyan-800",
                      transactional: "bg-emerald-100 text-emerald-800",
                      informational: "bg-slate-100 text-slate-700",
                      navigational: "bg-slate-100 text-slate-700",
                      comparison: "bg-purple-100 text-purple-800",
                    }[s.intent] || "bg-slate-100 text-slate-700";
                    return (
                      <tr key={i} className="border-t border-border hover:bg-secondary/20" data-testid={`ai-vis-sugg-row-${i}`}>
                        <td className="px-3 py-1.5 text-slate-400 tabular-nums">{i + 1}</td>
                        <td className="px-3 py-1.5 font-medium">{s.keyword}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${intentTone}`}>{s.intent}</span>
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm tabular-nums ${kdTone}`}>{kd.toFixed(0)}</span>
                        </td>
                        <td className="px-3 py-1.5 tabular-nums">{s.search_volume?.toLocaleString()}</td>
                        <td className="px-3 py-1.5 tabular-nums">${s.cpc_usd?.toFixed(2)}</td>
                        <td className="px-3 py-1.5 truncate max-w-[160px]">
                          <a href={s.competitor_url || `https://${s.top_competitor}`} target="_blank" rel="noreferrer noopener" className="text-cyan-700 hover:underline">
                            {s.top_competitor}
                          </a>
                        </td>
                        <td className="px-3 py-1.5 tabular-nums font-semibold text-emerald-700">{s.opportunity_score?.toLocaleString()}</td>
                        <td className="px-3 py-1.5 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 rounded-sm text-[10px] px-2"
                            onClick={() => copyKeyword(s.keyword)}
                            data-testid={`ai-vis-sugg-copy-${i}`}
                          >
                            <Copy size={10} className="mr-1" />Copy
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
